/**
 * FABTooltipReminder — periodic tooltip that nudges users about the FAB.
 *
 * Shows once per browser session, debounced to at most once every 12 hours
 * using localStorage timestamps. Auto-dismisses after DISPLAY_SECONDS and
 * can be closed manually.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import styles from './FABTooltipReminder.module.css';

const STORAGE_KEY = 'fab_tooltip_last_shown';
const DEBOUNCE_MS = 12 * 60 * 60 * 1000; // 12 hours
const DISPLAY_SECONDS = 10;
const INITIAL_DELAY_MS = 3000; // wait before showing so page settles

/** Check whether enough time has passed since the last display. */
export function shouldShowTooltip(): boolean {
  try {
    const last = localStorage.getItem(STORAGE_KEY);
    if (!last) return true;
    const elapsed = Date.now() - Number(last);
    return elapsed >= DEBOUNCE_MS;
  } catch {
    // localStorage blocked or unavailable — don't show
    return false;
  }
}

/** Record that the tooltip was just shown. */
export function markTooltipShown(): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // ignore write failures
  }
}

const FABTooltipReminder: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasShownRef = useRef(false);

  const dismiss = useCallback(() => {
    if (fadingOut) return;
    setFadingOut(true);
    setTimeout(() => setVisible(false), 300); // match fadeOut duration
    if (dismissTimer.current) {
      clearTimeout(dismissTimer.current);
      dismissTimer.current = null;
    }
  }, [fadingOut]);

  useEffect(() => {
    if (hasShownRef.current) return;
    if (!shouldShowTooltip()) return;

    const delayId = setTimeout(() => {
      if (hasShownRef.current) return;
      hasShownRef.current = true;
      markTooltipShown();
      setVisible(true);

      dismissTimer.current = setTimeout(() => {
        setFadingOut(true);
        setTimeout(() => setVisible(false), 300);
      }, DISPLAY_SECONDS * 1000);
    }, INITIAL_DELAY_MS);

    return () => clearTimeout(delayId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  return (
    <div
      className={`${styles.tooltip} ${fadingOut ? styles.fadeOut : ''}`}
      role="status"
      aria-live="polite"
    >
      <div className={styles.header}>
        <span className={styles.title}>
          <svg viewBox="0 0 20 20" width="17" height="17" fill="currentColor">
            <path d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 0-2 0 1 1 0 0 0 2 0ZM9 9a1 1 0 0 0 0 2v3a1 1 0 0 0 1 1h1a1 1 0 1 0 0-2v-3a1 1 0 0 0-1-1H9Z" />
          </svg>
          Did you know?
        </span>
        <button
          className={styles.closeBtn}
          onClick={dismiss}
          aria-label="Dismiss tooltip"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 4L4 12M4 4l8 8" />
          </svg>
        </button>
      </div>
      <div className={styles.body}>
        You can <span className={styles.highlight}>request new features</span>
        {' '}or <span className={styles.highlight}>report bugs</span> by
        clicking the Guru Cloud AI button below.
      </div>
      <div
        className={styles.progressBar}
        style={{ '--tooltip-duration': `${DISPLAY_SECONDS}s` } as React.CSSProperties}
      />
    </div>
  );
};

export default FABTooltipReminder;
